import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { HatGlasses, GraduationCap, UserSearch } from 'lucide-react-native';
import { memo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { type InquiryTypeBadge, MessageCard } from '@/components/inbox/MessageCard';
import {
  buildInquiryChatParams,
  deriveNavigationParams,
} from '@/components/inbox/inquiryChatParams';
import {
  ANONYMOUS_INQUIRY_BADGE_LABEL,
  MENTOR_INQUIRY_BADGE_LABEL,
  NO_EXPERT_INQUIRY_BADGE_LABEL,
} from '@/constants/inquiryDisplay';
import { useAnonymousInquiryGroupingEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { abilitySubject, useAbility } from '@/hooks/useAbility';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useModTeamName } from '@/hooks/useModTeamName';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { userIdAtom } from '@/services/globalState';
import { trackInquiryGroupOpened, type InboxTabContext } from '@/util/analyticsEvents';
import { isNoExpertInquiry } from '@/util/inquiryDisplay';
import type { InquiryGroupStateCounts } from '@/util/inquiryGroups';
import { isSameUserId } from '@/utils/isSameUserId';
import { getModDisplayForInbox, resolveModCardRouteLabel } from '@/utils/modHelpers';

const AUTHOR_PREFIX_PATTERN = /^[^:\n]{1,60}:\s/;

function hasAuthorPrefix(message: string) {
  return AUTHOR_PREFIX_PATTERN.test(message);
}

type InquiryCardProps = InquiryV2 & {
  currentTab: string;
  origin?: string | null;
  groupedInquiriesEnabled?: boolean;
  inquiryGroupCount?: number;
  inquiryGroupVisibleCount?: number;
  inquiryGroupStateBreakdown?: InquiryGroupStateCounts;
  hideSubject?: boolean;
  hideTimestamp?: boolean;
  groupExperts?: { name: string; avatarUrl?: string; variant?: 'route' }[];
  groupConversationIds?: string[];
  routeLabels?: string[];
};

/**
 * Inquiry card component that displays inquiry information and navigation.
 *
 * Uses public endpoint for pending OAuth users, private endpoint for authenticated users.
 * Wrapped with memo to prevent unnecessary re-renders when parent list updates.
 */
export const InquiryCard = memo(function InquiryCard(props: InquiryCardProps) {
  const {
    conversationId,
    title,
    lastResponseDate,
    author,
    recipient,
    inquiryStatus,
    origin,
    currentTab,
    groupedInquiriesEnabled,
    labels,
    hasUnread,
    lastMessagePreview,
    lastMessageAuthorId,
    inquiryGroupCount,
    inquiryGroupVisibleCount,
    inquiryGroupStateBreakdown,
    hideSubject,
    hideTimestamp,
    groupExperts,
    groupConversationIds,
    routeLabels,
  } = props;

  const effectiveUserId = useAtomValue(userIdAtom);

  const { isPrimumUser } = useCognitoGroups();

  const isCompactLayout = useIsCompactLayout();

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  /**
   * Resolve MOD display info here so the `MOD_DIRECT_TO_MSL`-aware hook
   * runs unconditionally. `getModDisplayForInbox` returns `null` for
   * non-MOD inquiries; the hook short-circuits to `undefined` in that case.
   */
  const modDisplay = recipient?.id
    ? getModDisplayForInbox(recipient.id, lastMessagePreview, title?.productIds)
    : null;

  const modTeamName = useModTeamName(modDisplay?.displayName);

  /**
   * Read from the server-stamped inquiry type rather than inferred from the
   * recipient, which would miss an anonymous submission: those are addressed to
   * the Ask Anonymously routing user, so the mentor is not the recipient.
   */
  const isMentorInquiry = title?.type === 'mentor_inquiry';

  const ability = useAbility();

  /**
   * Whether the chat will offer this reader "On your honor", by the same test the chat applies
   * once it loads. Passed to the chat so its loading skeleton can hold the control's place, and
   * the composer does not shift up when the real one arrives.
   */
  const canMarkOnYourHonor =
    !isSameUserId(author?.id, effectiveUserId) &&
    props.firstRespondedAt != null &&
    ability.can('attest', abilitySubject('Inquiry', props));

  const handleOpenConversation = useDebouncedCallback(
    () => {
      const chatParams = buildInquiryChatParams(props, currentTab, {
        effectiveUserId,
        isAnonymousInquiryGroupingEnabled,
        canMarkOnYourHonor,
        groupConversationIds,
      });

      if (!chatParams) {
        return;
      }

      const isGroupedInquiry = (inquiryGroupCount ?? 1) > 1;

      if (isGroupedInquiry) {
        Promise.resolve(
          trackInquiryGroupOpened({
            conversationId,
            inquiryParentId: props.parentId ?? props.id,
            inquiryGroupSize: inquiryGroupCount ?? inquiryGroupVisibleCount ?? 1,
            visibleInquiryGroupSize: inquiryGroupVisibleCount ?? inquiryGroupCount ?? 1,
            hasUnread: hasUnread ?? false,
            inboxTab: currentTab as InboxTabContext,
            inquiryGroupsEnabled: groupedInquiriesEnabled ?? false,
          })
        ).catch(() => undefined);
      }

      if (isCompactLayout) {
        router.push({ pathname: '/inbox/chat/[id]', params: chatParams });

        return;
      }

      router.setParams(chatParams);
    },
    600,
    { leading: true, trailing: false }
  );

  const derived = deriveNavigationParams(props, effectiveUserId, isAnonymousInquiryGroupingEnabled);

  if (!derived) {
    return null;
  }

  const {
    otherParticipant,
    titleExpert,
    anonymousPrimumRouting,
    isAskAnonymouslyRelayView,
    headerName: headerParticipantName,
    headerNameNoDoctor: headerParticipantNameWithoutDoctor,
    headerAvatarUrl: headerParticipantAvatarUrl,
    derivedName: name,
    nameWithoutDoctor,
    formalName,
    isSupportConversation,
    subject,
    avatarUrl,
  } = derived;

  /** Show submitter-facing anonymous hint copy only on grouped Ask Anonymously conversations. */
  const showGroupedAnonymousHint =
    anonymousPrimumRouting && !isAskAnonymouslyRelayView && isAnonymousInquiryGroupingEnabled;

  const isAuthorView = isSameUserId(author?.id, effectiveUserId);

  /**
   * One slot, so the kinds take precedence rather than stack. Mentor wins because it is the more
   * specific fact and can co-occur with anonymity; anonymous and no-expert are mutually exclusive
   * by construction.
   *
   * Mentor names a program and stays visible to everyone. The other two describe how the inquiry
   * was routed, which only matters to whoever works the queue, so the submitter is excluded: she
   * gets the privacy hint instead, which tells her something useful rather than naming the route.
   */
  let typeBadge: InquiryTypeBadge | undefined;

  if (isMentorInquiry) {
    typeBadge = { label: MENTOR_INQUIRY_BADGE_LABEL, icon: GraduationCap, tone: 'info' };
  } else if (isPrimumUser && isAskAnonymouslyRelayView) {
    typeBadge = { label: ANONYMOUS_INQUIRY_BADGE_LABEL, icon: HatGlasses, tone: 'info' };
  } else if (isPrimumUser && !isAuthorView && isNoExpertInquiry(props)) {
    typeBadge = { label: NO_EXPERT_INQUIRY_BADGE_LABEL, icon: UserSearch, tone: 'muted' };
  }

  const showPrimumPill = origin === 'primum';

  const isOtherParticipantMod = !titleExpert && isSameUserId(otherParticipant.id, recipient!.id);

  const displayName = isOtherParticipantMod ? (modTeamName ?? name) : name;

  const displayNameWithoutDoctor = isOtherParticipantMod
    ? (modTeamName ?? nameWithoutDoctor)
    : nameWithoutDoctor;

  const hasGroupedExperts = (groupExperts?.length ?? 0) > 1;

  /**
   * MOD Direct-to-MSL: surface the route as the card's secondary line on the
   * submitter view. Recipient view already renders routes as chips via
   * `routeLabels`, so the override is gated by `isOtherParticipantMod`.
   * Multi-route groups keep the existing `groupExperts` join (since
   * `hasGroupedExperts` puts MessageCard onto the inline-experts path).
   */
  const routeLabelForCard = resolveModCardRouteLabel(props, groupExperts);

  const submitterRouteName = isOtherParticipantMod ? routeLabelForCard : null;

  const cardDisplayName = isAskAnonymouslyRelayView
    ? headerParticipantName
    : (submitterRouteName ?? displayName);

  const cardDisplayNameWithoutDoctor = isAskAnonymouslyRelayView
    ? headerParticipantNameWithoutDoctor
    : (submitterRouteName ?? displayNameWithoutDoctor);

  const cardAvatarUrl = isAskAnonymouslyRelayView ? headerParticipantAvatarUrl : avatarUrl;

  const rawDisplayMessage = modDisplay?.displayMessage ?? lastMessagePreview ?? '';

  const displayMessage = rawDisplayMessage;

  const hasMessageAuthorPrefix = hasAuthorPrefix(displayMessage);

  const isLastMessageFromExpert =
    typeof lastMessageAuthorId === 'number' && lastMessageAuthorId !== author.id;

  const groupMessagePrefix = routeLabelForCard ?? formalName;

  const groupedDisplayMessage =
    hasGroupedExperts &&
    isLastMessageFromExpert &&
    !hasMessageAuthorPrefix &&
    displayMessage.trim().length > 0
      ? `${groupMessagePrefix}: ${displayMessage}`
      : displayMessage;

  return (
    <MessageCard
      id={conversationId}
      subject={modDisplay?.displaySubject ?? subject ?? '--'}
      lastRespondedAt={new Date(lastResponseDate || '')}
      name={cardDisplayName}
      nameWithoutDoctor={cardDisplayNameWithoutDoctor}
      hideName={isSupportConversation || isMentorInquiry}
      showSupportBadgeInline={isSupportConversation}
      avatarUrl={cardAvatarUrl}
      message={groupedDisplayMessage}
      unread={hasUnread ?? false}
      onPress={handleOpenConversation}
      isMessageLoaded
      inquiryStatus={inquiryStatus}
      currentTab={currentTab}
      isPrimum={showPrimumPill}
      productLabel={modDisplay?.productLabel}
      typeBadge={typeBadge}
      labels={labels}
      inquiryGroupCount={inquiryGroupCount}
      inquiryGroupVisibleCount={inquiryGroupVisibleCount}
      inquiryGroupStateBreakdown={inquiryGroupStateBreakdown}
      hideSubject={hideSubject}
      hideTimestamp={hideTimestamp}
      groupExperts={groupExperts}
      routeLabels={routeLabels}
      selectedConversationIds={groupConversationIds}
      anonymousPrimumRouting={anonymousPrimumRouting}
      askAnonymouslyRelayView={isAskAnonymouslyRelayView}
      showGroupedAnonymousHint={showGroupedAnonymousHint}
    />
  );
});
